import { Army } from "@/domain/army";
import { getIllustrationSrc, getImageUploadSrc } from "@/domain/s3-urls";
import { Unit } from "@/domain/unit";
import { ArmyImage } from "@/schema/latest";
import { useArmyOp } from "@/ui/army-provider/context";
import { Button } from "@/ui/button";
import { KnownErrorMessage } from "@/ui/db-error";
import { RulesetProvider } from "@/ui/game-content-provider";
import { useDialog } from "@/ui/hooks/use-dialog";
import { Image } from "@/ui/image";
import { Link } from "@/ui/link";
import { ListView } from "@/ui/list-view";
import { LoadingIndicator } from "@/ui/loading-indicator";
import { Paragraphs } from "@/ui/paragraphs";
import { ShareArmyDialog } from "@/ui/share-army-dialog";
import { AddUnitDialog } from "@/ui/unit-form-dialog";
import { button, interactiveText, primaryTheme } from "@/ui/utils.css";
import { DialogDisclosure } from "@ariakit/react";
import { useCurrentUser, useMediaQuery } from "@indietabletop/appkit";
import { ReactNode, useState } from "react";
import { Route, Switch, useParams, useRoute } from "wouter";
import { ArmyActionsMenu, ChangePosterImageButton } from "../army-actions-menu";
import { ArmyIssues } from "../army-issues";
import { DialogTrigger } from "../dialog-trigger";
import { KeywordsHandler } from "../keywords-handler";
import { LocalArmyProvider } from "../local-army-provider";
import { MinWidth } from "../media";
import { UnitMiniIcons } from "../mini-icon";
import { MobileNavLayout } from "../mobile-nav-layout";
import { MusterArmyDialog } from "../MusterArmyDialog/MusterArmyDialog";
import { hrefs } from "../root-layout/fmt";
import { color } from "../tokens";
import { UnitActionsMenu } from "../unit-actions-menu";
import { UnitCount } from "../unit-count";
import { UnitDetailView } from "../unit-detail-page";
import { UpgradeArmyRulesetDialog } from "../upgrade-army-ruleset-dialog";
import { BackButton, CloseIcon, DesktopViewHeader } from "./desktop-view-header";
import { armyPhotoSize } from "./imageSizes";
import * as css from "./style.css";

export function ArmyDetailUnitCard(props: { unit: Unit; href: string }) {
  const [match] = useRoute(props.href);
  const { unit } = props;

  return (
    <Link href={props.href} className={css.unitLink} aria-current={match ? "true" : "false"}>
      {unit.image && (
        <Image
          optimize
          src={getImageUploadSrc(unit.image.src)}
          width={320}
          height={180}
          className={css.unitImage}
        />
      )}

      <div className={css.unitContent}>
        <h2 className={css.unitHeading}>
          {unit.name}

          <UnitCount count={unit.count} />

          <UnitMiniIcons keywords={unit.keywords} />
        </h2>

        <div className={css.unitSummary({ isTrunaced: !!unit.image })}>
          {!unit.isValid() && (
            <>
              <span style={{ color: color.PURPLE }}>Invalid</span>
              {" · "}
            </>
          )}
          <span>{unit.summary}</span>

          {unit.keywords.length > 0 && (
            <span className={css.unitKeywords} style={{ marginTop: "0.25rem" }}>
              {" · "}
              {unit.keywordsSummary}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function AddUnitButton() {
  const armyOp = useArmyOp();

  if (!armyOp.isSuccess) {
    return null;
  }

  return (
    <DialogTrigger>
      <AddUnitDialog army={armyOp.value} />

      <DialogDisclosure className={button} style={primaryTheme}>
        Add Unit
      </DialogDisclosure>
    </DialogTrigger>
  );
}

export function MusterArmyButton() {
  const armyOp = useArmyOp();

  if (!armyOp.isSuccess || !armyOp.value.isCollection) {
    return null;
  }

  return (
    <DialogTrigger>
      <MusterArmyDialog collection={armyOp.value} />

      <DialogDisclosure className={button} style={primaryTheme}>
        Muster Army
      </DialogDisclosure>
    </DialogTrigger>
  );
}

function ShareArmyButton() {
  const armyOp = useArmyOp();
  const { isOpen, setOpen, store } = useDialog();

  return (
    <>
      {isOpen && armyOp.isSuccess && (
        <ShareArmyDialog
          store={store}
          army={armyOp.value}
          armyShareTitle={`${armyOp.value.name} (${armyOp.value.summary})`}
        />
      )}

      <button className={css.quickActionButton} type="button" onClick={setOpen}>
        Share
      </button>
    </>
  );
}

export function ImageUploadCoverImage(props: { src: string }) {
  return (
    <div className={css.armyPhotoContainer}>
      <Image
        {...armyPhotoSize}
        className={css.armyPhoto}
        src={getImageUploadSrc(props.src)}
        alt=""
        optimize
      />
    </div>
  );
}

function CoverImage(props: { image: ArmyImage }) {
  if (props.image.type === "ILLUSTRATION") {
    return (
      <Image
        src={getIllustrationSrc(props.image.id)}
        alt=""
        width={600}
        height={600}
        className={css.armyIllustration}
        optimize
      />
    );
  }

  return <ImageUploadCoverImage src={props.image.src} />;
}

function UpdateArmyRulesetButton() {
  const armyOp = useArmyOp();
  const { store, isOpen, setOpen } = useDialog();

  return (
    <>
      {isOpen && armyOp.isSuccess && <UpgradeArmyRulesetDialog store={store} army={armyOp.value} />}

      <Button className={interactiveText} onClick={setOpen}>
        Upgrade ruleset?
      </Button>
    </>
  );
}

export function ArmyDetailView(props: {
  stickyButton?: ReactNode;
  actionsMenu?: ReactNode;
  updateArmyButton?: ReactNode;
}) {
  const armyOp = useArmyOp();

  if (armyOp.isPending) {
    return <LoadingIndicator />;
  }

  if (armyOp.isFailure) {
    return <KnownErrorMessage error={armyOp.failure} />;
  }

  const army = armyOp.value;

  const { mainImage } = army;

  const centeredLayout = mainImage?.type === "ILLUSTRATION";

  return (
    <RulesetProvider ruleset={army.ruleset}>
      <KeywordsHandler>
        <div className={css.armyView}>
          <div className={css.view}>
            <div className={css.cover}>
              <DesktopViewHeader
                label={army.typeLabel}
                menu={props.actionsMenu}
                back={<BackButton href="~/army" />}
              />

              <header
                className={css.armyHeader({
                  centerText: centeredLayout,
                })}
              >
                {mainImage && <CoverImage image={mainImage} />}

                <h1 className={css.armyName}>{army.name}</h1>
                <div className={css.armySummary}>{army.summary}</div>
              </header>

              {!army.isShared && (
                <div className={css.armyQuickActions({ isCentered: centeredLayout })}>
                  <ShareArmyButton />

                  <Link className={css.quickActionButton} href={`~/print/a/${army.id}`}>
                    Print
                  </Link>
                </div>
              )}

              <ArmyIssues army={army} updateArmyButton={props.updateArmyButton} />

              {army.description && (
                <div className={css.description}>
                  <Paragraphs text={army.description} />
                </div>
              )}
            </div>

            <ListView
              items={army.units.map((unit) => (
                <ArmyDetailUnitCard href={`/u/${unit.id}`} key={unit.id} unit={unit} />
              ))}
              stickyButton={props.stickyButton}
            />
          </div>
        </div>
      </KeywordsHandler>
    </RulesetProvider>
  );
}

function UnitEmptyState(props: { army: Army | null }) {
  const posterImage = props.army?.posterImage;
  const changeButton = props.army && !props.army.isShared && (
    <div className={css.unitEmptyStateButton}>
      <ChangePosterImageButton army={props.army} />
    </div>
  );

  if (posterImage) {
    return (
      <div className={css.unitEmptyState}>
        <Image
          key="poster"
          src={getImageUploadSrc(posterImage.src)}
          style={{ inlineSize: "100%", blockSize: "100%", objectFit: "cover" }}
          alt=""
          width={1200}
          height={1600}
          optimize
        />
        {changeButton}
      </div>
    );
  }

  return (
    <div className={css.unitEmptyState}>
      <Image
        key="placeholder"
        src="/images/magic_banner.webp"
        alt=""
        width={600}
        height={400}
        className={css.unitEmptyStateImage}
      />
      <div className={css.unitEmptyStateHint}>Select a unit</div>
      {changeButton}
    </div>
  );
}

export function ExplainerDialog() {
  const currentUser = useCurrentUser();
  const [open, setOpen] = useState(!currentUser);

  if (!open) {
    return null;
  }

  return (
    <div className={css.explainer.container}>
      <div className={css.explainer.header}>
        <h2 className={css.explainer.heading}>Hello there! 👋</h2>
        <Button onClick={() => setOpen(false)} className={css.explainer.close}>
          <CloseIcon alt="Close" />
        </Button>
      </div>

      <p className={css.explainer.info}>
        Curious about <em>Hobgoblin</em>? Wanna try the <em>Quick Start Rules</em> or build your own army?
      </p>

      <Link href={hrefs.about()} className={css.explainer.action}>
        Learn more
      </Link>
    </div>
  );
}

export function ArmyPageLayout(props: {
  armyDetail: ReactNode;
  armyNavAction?: ReactNode;
  unitTitle: string;
  unitDetail: ReactNode;
  unitNavAction?: ReactNode;
  explainer?: ReactNode;
}) {
  const isWideViewport = useMediaQuery(MinWidth.WIDE);
  const army = useArmyOp().valueOrNull();

  if (!isWideViewport) {
    return (
      <Switch>
        <Route path="/">
          <MobileNavLayout
            title={army?.shortTypeLabel}
            action={props.armyNavAction}
            backHref="~/army"
          >
            {props.armyDetail}

            {props.explainer}
          </MobileNavLayout>
        </Route>

        <Route path="/u/:unitId">
          <MobileNavLayout title={props.unitTitle} action={props.unitNavAction} backHref="/">
            {props.unitDetail}
          </MobileNavLayout>
        </Route>
      </Switch>
    );
  }

  return (
    <div className={css.viewsContainer}>
      <div className={css.viewTrack}>{props.armyDetail}</div>

      <div className={css.viewTrack}>
        <div className={css.unitView}>
          <Switch>
            <Route path="/u/:unitId">{props.unitDetail}</Route>

            <Route>
              <UnitEmptyState army={army} />
            </Route>
          </Switch>
        </div>
      </div>

      {props.explainer}
    </div>
  );
}

export function ArmyPages() {
  const params = useParams<{ armyId: string }>();

  return (
    <LocalArmyProvider armyId={params.armyId}>
      <ArmyPageLayout
        armyNavAction={<ArmyActionsMenu iconColor="white" />}
        armyDetail={
          <ArmyDetailView
            stickyButton={
              <>
                <AddUnitButton />
                <MusterArmyButton />
              </>
            }
            updateArmyButton={<UpdateArmyRulesetButton />}
            actionsMenu={<ArmyActionsMenu />}
          />
        }
        unitTitle="Unit"
        unitNavAction={<UnitActionsMenu iconColor="white" />}
        unitDetail={<UnitDetailView headerLabel="Unit" />}
      />
    </LocalArmyProvider>
  );
}
